import Vec2 from 'vec2';

import { MathFunctions } from '@common/helpers';
import { DrawingShape } from '@common/models';
import { letterLabelToIndex } from '@common/utils/indexToLetterLabel';

/**
 * @deprecated Use {@link getLetterIndexFromDrawingEdges}. This assumes the edges will always be in (a,b), (b,c), (c,d), (d,e) order which is not always true
 * @summary returns the index of top "edge" that would be created from a set of points
 * @param points
 */
export const getLetterStartIndex = (points: Vec2[]) => {
    let minTop = Number.MAX_SAFE_INTEGER;
    let minTopIndex = 0;

    for (let i = 1; i < points.length; i++) {
        const startPt = points[i - 1];
        const endPt = points[i];
        const midPt = MathFunctions.getMidpoint(startPt, endPt);

        if (midPt.y < minTop) {
            minTop = midPt.y;
            minTopIndex = i - 1;
        }
    }

    return minTopIndex;
};

interface TopmostEdge {
    minY: number;
    index: number;
}

/**
 * returns the index of the "topmost" drawingEdge with the "highest" (visually) midPoint.y value
 * @param shape
 */
export const getLetterStartFromDrawingEdges = (shape: DrawingShape): number => {
    const corners = Object.values(shape.cornerMap);
    const topEdge = shape.drawingEdges.reduce(
        (result, edge, edgeIndex) => {
            const startPt = corners.find((corner) => corner.id === edge.startCornerId);
            const endPt = corners.find((corner) => corner.id === edge.endCornerId);
            if (!startPt || !endPt) {
                return result;
            }
            const midPt = MathFunctions.getMidpoint(startPt.coords, endPt.coords);
            return midPt.y < result.minY ? ({ minY: midPt.y, index: edgeIndex } as TopmostEdge) : result;
        },
        { minY: Number.MAX_SAFE_INTEGER, index: 0 } as TopmostEdge
    );
    return topEdge.index;
};

/**
 * Work backward from a "removedEdlementsHistory" to find which arrow the "A" should be.
 * Old estimates (pre 2024-10-16) did not save establishedLetterStartIndex, but if they set a side option on any
 * edge, we can back into where "A" must be.  @see EZE-1500
 */
export const getInitialLabelIndexFromRemovedHistory = (dShape: DrawingShape): number | undefined => {
    if (!dShape.removedElementsHistory?.edgesAndCorners.length || !dShape.arrowsInfo?.length) {
        return;
    }
    const lowestLabelInfo = dShape.removedElementsHistory.edgesAndCorners
        .filter((removedEdge) => !!removedEdge.arrowInfo?.label && removedEdge.drawingEdge?.id)
        .map((removedEdge) => ({
            label: removedEdge.arrowInfo?.label,
            edgeId: removedEdge.drawingEdge.id,
        }))
        .toSorted((arrowInfoA, arrowInfoB) => {
            const label1 = arrowInfoA.label ?? '';
            const label2 = arrowInfoB.label ?? '';
            // Sort by length and then alphabetically
            return Math.sign(label1.length - label2.length) || label1.localeCompare(label2);
        })[0];
    if (!lowestLabelInfo) {
        return undefined; // This could happen if the arrows don't have any labels yet - probably broken
    }
    const indexOfLowestLetter = letterLabelToIndex(lowestLabelInfo.label);
    const startIndex = dShape.arrowsInfo.findIndex((arrowInfo) => arrowInfo.drawingEdgeId === lowestLabelInfo.edgeId);

    // iterate through all the arrows and skip diagonals and interiors to find the index of "A"
    // SEE: Drawingtools!!!
    let countDown = indexOfLowestLetter;
    let indexOfA = startIndex;
    while (countDown) {
        indexOfA = indexOfA + 1 >= dShape.arrowsInfo.length ? 0 : indexOfA + 1;
        const isLetterSide = !(dShape.arrowsInfo[indexOfA].isInterior || dShape.arrowsInfo[indexOfA].isDiagonalCorner);
        countDown = countDown - Number(isLetterSide); // only continue if it's a lettered side
    }
    return indexOfA;
};
